import { useMemo } from 'react'
import {
	BenchmarkScale,
	BenchmarkScaleParam,
} from '@/lib/enums/bi-enums'

/**
 * A mapping object that associates each `BenchmarkScale` enum value
 * with its corresponding `BenchmarkScaleParam` value.
 *
 * This map is used to resolve benchmark scales to their parameters
 * for consistent handling of benchmark-related data.
 *
 * @typeParam BenchmarkScale - Enum representing the different benchmark scales.
 * @typeParam BenchmarkScaleParam - Enum representing the parameters associated with each benchmark scale.
 */
export const benchmarkMap: { [key in BenchmarkScale]: BenchmarkScaleParam } = {
	[BenchmarkScale.LessThan1M]: BenchmarkScaleParam.LessThan1M,
	[BenchmarkScale.Upto5M]: BenchmarkScaleParam.Upto5M,
	[BenchmarkScale.Upto10M]: BenchmarkScaleParam.Upto10M,
	[BenchmarkScale.Upto20M]: BenchmarkScaleParam.Upto20M,
	[BenchmarkScale.MoreThan20M]: BenchmarkScaleParam.MoreThan20M,
	[BenchmarkScale.Unknown]: BenchmarkScaleParam.Unknown,
}

/**
 * A mapping between `BenchmarkScaleParam` and `BenchmarkScale`.
 * This object provides a direct correlation between the parameter
 * used for benchmarking and its corresponding scale.
 *
 * @constant
 * @type {Record<BenchmarkScaleParam, BenchmarkScale>}
 *
 * @example
 * ```typescript
 * const scale = benchmarkParamToScaleMap[BenchmarkScaleParam.LessThan1M];
 * console.log(scale); // Outputs: BenchmarkScale.LessThan1M
 * ```
 */
export const benchmarkParamToScaleMap: Record<
	BenchmarkScaleParam,
	BenchmarkScale
> = {
	[BenchmarkScaleParam.LessThan1M]: BenchmarkScale.LessThan1M,
	[BenchmarkScaleParam.Upto5M]: BenchmarkScale.Upto5M,
	[BenchmarkScaleParam.Upto10M]: BenchmarkScale.Upto10M,
	[BenchmarkScaleParam.Upto20M]: BenchmarkScale.Upto20M,
	[BenchmarkScaleParam.MoreThan20M]: BenchmarkScale.MoreThan20M,
	[BenchmarkScaleParam.Unknown]: BenchmarkScale.Unknown,
}

/**
 * A custom hook that resolves a benchmark scale value based on the provided input.
 * 
 * This hook uses `useMemo` to ensure the returned value is computed efficiently
 * and remains consistent across renders unless the input changes.
 * 
 * @param benchmarkRadioValue - The input benchmark scale value to resolve.
 * @returns The resolved benchmark scale value if it exists and is not `Unknown`,
 *          otherwise `undefined`.
 */
export default function useResolvedBenchmarkScale(
	benchmarkRadioValue: BenchmarkScale
) {
	return useMemo(() => {
		const mapped = benchmarkMap[benchmarkRadioValue];
		// Return mapped value only if it exists and is not Unknown
		return (mapped && mapped !== BenchmarkScaleParam.Unknown) ? mapped : undefined;
	}, [benchmarkRadioValue]);
}
